import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import VariantMap from './EmailCaptureVariants';

import { setFormAsSubmitted, formWasSubmitted } from './utils/storageUtils';
import { isValidEmail } from '../../utils/strings';

import './EmailCapture.css';

export default class EmailCapture extends React.Component {
  constructor(props) {
    super(props);

    const { delay } = props;
    const isVisible = delay && delay <= 0;

    this.state = {
      isVisible,
      isSubmitting: false,
      isSuccess: false,
      formFieldValues: {},
      errors: {},
    };

    this.clearError = this.clearError.bind(this);
    this.getErrors = this.getErrors.bind(this);
    this.clearInputs = this.clearInputs.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.updateFormFields = this.updateFormFields.bind(this);
    this.closeEmailCapture = this.closeEmailCapture.bind(this);
    this.updateVisibility = this.updateVisibility.bind(this);
    this.toggleVisibility = this.toggleVisibility.bind(this);
    this.formOnSuccess = this.formOnSuccess.bind(this);
  }

  componentDidMount() {
    const { locationId } = this.props;
    // If it hasn't been submitted, then display
    if (!formWasSubmitted(locationId)) {
      this.toggleVisibility();
    }
  }

  getErrors() {
    const { fields } = this.props;
    const { formFieldValues } = this.state;
    const errors = {};

    fields.forEach(field => {
      const { key } = field;
      const value = formFieldValues[key];
      if (!value) {
        errors[key] = 'Please enter a valid value';
      }
      if (key === 'email' && !isValidEmail(value)) {
        errors[key] = 'Please enter a valid email';
      }
    });
    return errors;
  }

  clearError(key) {
    const { errors } = this.state;
    delete errors[key];
    this.setState({ errors });
  }

  formOnSuccess() {
    const { closeDelay, locationId } = this.props;

    this.setState({
      isSubmitting: false,
      isSuccess: true,
      formFieldValues: {},
    });

    setFormAsSubmitted(locationId);
    this.closeEmailCapture(closeDelay * 1000);
  }

  updateVisibility(isVisible) {
    this.setState({ isVisible });
  }

  clearInputs() {
    this.setState({ formFieldValues: {} });
  }

  toggleVisibility() {
    const { isVisible } = this.state;
    const { delay } = this.props;

    if (delay) {
      setTimeout(() => this.setState({ isVisible: !isVisible }), delay * 1000);
    }
  }

  submitForm(event) {
    event.stopPropagation();
    const { locationId, businessId, businessType } = this.props;
    const { isSubmitting } = this.state;
    const headers = {};

    if (isSubmitting) {
      return;
    }

    this.setState({ isSubmitting: true });
    const { formFieldValues } = this.state;

    const errors = this.getErrors();
    this.setState({ errors });
    if (Object.keys(errors).length) {
      this.setState({ isSubmitting: false });
      return;
    }

    formFieldValues.location = locationId;

    const url = process.env.SERVER_URL || process.env.STORYBOOK_SERVER_URL;
    const fullUrl = `${url}businesses/${businessType}s/${businessId}/locations/${locationId}/contacts/create/`;

    axios
      .post(fullUrl, formFieldValues, { headers })
      .then(() => {
        this.formOnSuccess();
      })
      .catch(({ response: { status } }) => {
        if (status === 409) {
          this.formOnSuccess();
        } else {
          this.setState({ isSubmitting: false });
        }
      });
  }

  closeEmailCapture(ms = 0) {
    setTimeout(() => {
      this.clearInputs();
      this.updateVisibility(false);
    }, ms);
  }

  updateFormFields(key, event) {
    const { formFieldValues } = this.state;
    formFieldValues[key] = event.target.value;
    this.clearError(key);
    this.setState({ formFieldValues });
  }

  render() {
    const { isVisible, isSubmitting, isSuccess, formFieldValues, errors } = this.state;
    const { type, className, ...rest } = this.props;
    const Body = VariantMap[type];

    return (
      <div className={`fc-email-capture${className ? ` ${className}` : ''}`}>
        <Body
          type={type}
          submitForm={this.submitForm}
          closeEmailCapture={this.closeEmailCapture}
          updateFormFields={this.updateFormFields}
          isVisible={isVisible}
          isSubmitting={isSubmitting}
          formFieldValues={formFieldValues}
          isSuccess={isSuccess}
          errors={errors}
          {...rest}
        />
      </div>
    );
  }
}

EmailCapture.propTypes = {
  locationId: PropTypes.number.isRequired,
  businessId: PropTypes.number.isRequired,
  businessType: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['inline', 'banner', 'modal']),
  headerText: PropTypes.string,
  subheaderText: PropTypes.string,
  description: PropTypes.string,
  descriptionTextColor: PropTypes.string,
  textColor: PropTypes.string,
  bgColor: PropTypes.string,
  delay: PropTypes.number,
  closeDelay: PropTypes.number,
  image: PropTypes.shape({
    url: PropTypes.string,
    placement: PropTypes.oneOf(['left', 'right', 'top', 'background']),
  }),
  darkCloseBtn: PropTypes.bool,
  button: PropTypes.shape({
    label: PropTypes.string,
    key: PropTypes.string,
    primary: PropTypes.bool,
    bgColor: PropTypes.string,
    textColor: PropTypes.string,
  }),
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      key: PropTypes.oneOf(['email', 'first_name', 'last_name', 'phone']),
    }),
  ),
};

EmailCapture.defaultProps = {
  type: 'inline',
  headerText: 'Join our Mailing List',
  subheaderText: '',
  description: '',
  descriptionTextColor: 'black',
  textColor: 'black',
  bgColor: 'white',
  delay: 2,
  closeDelay: 4,
  image: null,
  darkCloseBtn: true,
  button: {
    label: 'Continue',
    key: 'clickme',
    primary: true,
    bgColor: '',
    textColor: '',
  },
  fields: [
    {
      label: 'Email',
      key: 'email',
    },
  ],
};
